import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { 
  CreditCard, Building, Save, 
  Loader2, AlertCircle, X, User, FileText
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import "./PaymentType.css";

const url = process.env.REACT_APP_BACKEND_URL;

const EditPaymentType = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userType, company } = useAuth();

  const [formData, setFormData] = useState({
    paymentstype: "",
    description: "",
    status: "active",
  });
  const [paymentTypeDoc, setPaymentTypeDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState({});

  const fetchPaymentType = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/api/paymenttype/${id}`);
      
      if (res.data.success) {
        setPaymentTypeDoc(res.data.data);
        setFormData({
          paymentstype: res.data.data.paymentstype || "",
          description: res.data.data.description || "",
          status: res.data.data.status || "active",
        });
      } else {
        toast.error(res.data.message || "Failed to load payment type");
        navigate("/ViewPaymentType");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load payment type");
      navigate("/ViewPaymentType");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentType();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleFocus = (field) => setFocused({ ...focused, [field]: true });
  const handleBlur = (field) => {
    setFocused({ 
      ...focused, 
      [field]: formData[field] && formData[field].length > 0 
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.paymentstype.trim()) {
      newErrors.paymentstype = "Payment type name is required";
    } else if (formData.paymentstype.trim().length < 2) {
      newErrors.paymentstype = "Payment type name must be at least 2 characters";
    } else if (formData.paymentstype.trim().length > 100) {
      newErrors.paymentstype = "Payment type name cannot exceed 100 characters";
    }
    
    if (formData.description && formData.description.trim().length > 500) {
      newErrors.description = "Description cannot exceed 500 characters";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    
    setErrors({});
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }

    setSubmitting(true);
    try {
      const updateData = {
        paymentstype: formData.paymentstype.trim(),
        description: formData.description ? formData.description.trim() : "",
        status: formData.status
      };
      
      const res = await axios.put(`${url}/api/paymenttype/${id}`, updateData);
      
      if (res.data.success) {
        toast.success("Payment type updated successfully!");
        
        setTimeout(() => {
          navigate("/ViewPaymentType");
        }, 1000);
      } else {
        toast.error(res.data.message || "Update failed");
        setSubmitting(false);
      }
    } catch (err) {
      if (err.response) {
        const errorMsg = err.response.data?.message || 
                        err.response.data?.error || 
                        "Update failed";
        toast.error(errorMsg);
        
        if (err.response.status === 409) {
          setErrors(prev => ({
            ...prev,
            paymentstype: "Payment type name already exists for this company"
          }));
        }
        
        if (err.response.data?.errors) {
          const newErrors = {};
          err.response.data.errors.forEach(error => {
            newErrors[error.field] = error.message;
          });
          setErrors(prev => ({ ...prev, ...newErrors }));
        }
      } else if (err.request) {
        toast.error("No response from server. Please check your connection.");
      } else {
        toast.error("Error: " + err.message);
      }
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/ViewPaymentType");
  };

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="paymenttype-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading payment type details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!paymentTypeDoc) {
    return (
      <div className="form-wrapper">
        <div className="paymenttype-form-container">
          <div className="error-container">
            <AlertCircle size={32} color="#dc3545" />
            <p>Payment type not found</p>
            <button 
              className="cancel-btn" 
              onClick={() => navigate("/ViewPaymentType")}
            >
              Back to List
            </button>
          </div>
        </div>
      </div>
    );
  }

  const canEdit = formData.status === "active";

  return (
    <div className="form-wrapper">
      <div className="paymenttype-form-container">
        <button
          className="close-btn"
          onClick={handleCancel}
          title="Go Back"
          disabled={submitting}
        >
          <X size={20} />
        </button>

        <div className="form-header">
          <CreditCard size={24} />
          <h2 className="form-title">Edit Payment Type</h2>
          <p className="form-subtitle">
            Update payment type details for {paymentTypeDoc?.paymentstype || 'payment type'}
          </p>
        </div>

        <form className="paymenttype-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            {/* Payment Type Name */}
            <div className={`form-field required ${focused.paymentstype || formData.paymentstype ? "filled" : ""} ${errors.paymentstype ? 'error' : ''}`}>
              <input
                type="text"
                name="paymentstype"
                value={formData.paymentstype}
                onChange={handleChange}
                onFocus={() => handleFocus("paymentstype")}
                onBlur={() => handleBlur("paymentstype")}
                placeholder="Enter payment type name"
                required
                maxLength={100}
                disabled={!canEdit || submitting}
              />
              <label>
                <CreditCard size={14} className="mr-2" />
                Payment Type Name *
              </label>
              {errors.paymentstype && <small className="field-error">{errors.paymentstype}</small>}
              <small className="field-hint">
                Unique name for this payment type within the company
              </small>
            </div>

            {/* Description */}
            <div className={`form-field ${focused.description || formData.description ? "filled" : ""} ${errors.description ? 'error' : ''}`}>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                onFocus={() => handleFocus("description")}
                onBlur={() => handleBlur("description")}
                placeholder="Enter description (optional)"
                rows="3"
                maxLength={500}
                disabled={!canEdit || submitting}
              />
              <label>
                <FileText size={14} className="mr-2" />
                Description
              </label>
              {errors.description && <small className="field-error">{errors.description}</small>}
              <small className="field-hint">
                Optional - Brief description of this payment type
              </small>
            </div>

            {/* Status */}
            <div className="form-field">
              <div className="select-wrapper">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  disabled={submitting}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <AlertCircle size={16} className="select-icon" />
              </div>
              <label>Status</label>
              <small className="field-hint">
                {formData.status === 'active' 
                  ? "Active payment types can be used in transactions"
                  : "Inactive payment types cannot be used in transactions"
                }
              </small>
            </div>

            {/* Warning if editing inactive payment type */}
            {formData.status === 'inactive' && (
              <div className="warning-message">
                <AlertCircle size={16} />
                <span>Inactive payment types cannot be edited. Change status to active to make changes.</span>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || (!canEdit && formData.status === 'inactive')}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Updating...
                </>
              ) : (
                <>
                  Update Payment Type
                  <Save className="ml-2" size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPaymentType;